import { shapePointsExceedMaxSeparation } from '@shared/helpers/geometry/point-separation.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';
import type { Point2D } from '@shared/models/geometry.model';

/** Shared strict tolerance for the lit right-angle bracket and release snapping. */
export const RIGHT_ANGLE_INDICATOR_TOLERANCE_DEGREES = 0.5;

const EPSILON = 1e-6;
const ANGLE_TOLERANCE_DEGREES = 1e-4;
const RIGHT_ANGLE_COMPLETION_TOLERANCE_DEGREES = 1e-4;
const MIN_ANGLE_DEGREES = 0.1;
const MAX_ANGLE_DEGREES = 179.9;
const MIN_SEGMENT_LENGTH_METERS = 0.1;

export type ManualCornerAngleTimestamps = ReadonlyMap<number, number>;

export type CornerAngleEditPlan = {
  pointIndex: number;
  nextPoint: Point2D;
};

export type MultipleCornerRightAnglePlan = {
  nextPoints: Point2D[];
  pointIndexes: number[];
};

const distance = (first: Point2D, second: Point2D): number =>
  Math.hypot(second[0] - first[0], second[1] - first[1]);

export const getCornerAngleDegrees = (
  vertex: Point2D,
  previous: Point2D,
  next: Point2D,
): number | undefined => {
  const firstX = previous[0] - vertex[0];
  const firstY = previous[1] - vertex[1];
  const secondX = next[0] - vertex[0];
  const secondY = next[1] - vertex[1];
  const firstLength = Math.hypot(firstX, firstY);
  const secondLength = Math.hypot(secondX, secondY);
  if (firstLength < EPSILON || secondLength < EPSILON) return undefined;
  const cosine = Math.max(
    -1,
    Math.min(
      1,
      (firstX * secondX + firstY * secondY) / (firstLength * secondLength),
    ),
  );
  return (Math.acos(cosine) * 180) / Math.PI;
};

export const getPolygonCornerAngleDegrees = (
  points: readonly Point2D[],
  pointIndex: number,
): number | undefined => {
  if (points.length < 3) return undefined;
  const vertex = points[pointIndex];
  const previous = points[(pointIndex - 1 + points.length) % points.length];
  const next = points[(pointIndex + 1) % points.length];
  return vertex && previous && next
    ? getCornerAngleDegrees(vertex, previous, next)
    : undefined;
};

export const formatCornerAngleDegrees = (value: number): string | undefined =>
  Number.isFinite(value) ? String(Math.round(value)) : undefined;

export const parseCornerAngleInput = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) &&
    parsed >= MIN_ANGLE_DEGREES &&
    parsed <= MAX_ANGLE_DEGREES
    ? parsed
    : undefined;
};

const rotate = (direction: Point2D, radians: number): Point2D => {
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return [
    direction[0] * cosine - direction[1] * sine,
    direction[0] * sine + direction[1] * cosine,
  ];
};

const rayCircleIntersections = (input: {
  origin: Point2D;
  direction: Point2D;
  center: Point2D;
  radius: number;
}): Point2D[] => {
  const offsetX = input.origin[0] - input.center[0];
  const offsetY = input.origin[1] - input.center[1];
  const projection =
    offsetX * input.direction[0] + offsetY * input.direction[1];
  const constant =
    offsetX * offsetX + offsetY * offsetY - input.radius * input.radius;
  const discriminant = projection * projection - constant;
  if (discriminant < -EPSILON) return [];
  const root = Math.sqrt(Math.max(0, discriminant));
  return [-projection - root, -projection + root].flatMap((ratio) =>
    ratio >= MIN_SEGMENT_LENGTH_METERS
      ? [
          [
            input.origin[0] + input.direction[0] * ratio,
            input.origin[1] + input.direction[1] * ratio,
          ] as Point2D,
        ]
      : [],
  );
};

const getPreservedAngleRays = (
  vertex: Point2D,
  fixedNeighbor: Point2D,
  angleDegrees: number,
): Point2D[] => {
  const dx = fixedNeighbor[0] - vertex[0];
  const dy = fixedNeighbor[1] - vertex[1];
  const length = Math.hypot(dx, dy);
  if (!(length > EPSILON)) return [];
  const direction: Point2D = [dx / length, dy / length];
  const radians = (angleDegrees * Math.PI) / 180;
  return [rotate(direction, radians), rotate(direction, -radians)];
};

/**
 * Move only one corner while its two neighboring vertices stay fixed.
 * Candidate points lie on the constant-angle circle through both neighbors.
 * Manually entered neighboring angles are compared newest-first, so an older
 * soft lock is sacrificed before a newer one when both cannot remain exact.
 */
export const planCornerAngleEdit = (input: {
  points: readonly Point2D[];
  pointIndex: number;
  angleDegrees: number;
  manualAngleTimestamps?: ManualCornerAngleTimestamps;
}): CornerAngleEditPlan | undefined => {
  const { points, pointIndex, angleDegrees } = input;
  if (
    points.length < 3 ||
    pointIndex < 0 ||
    pointIndex >= points.length ||
    parseCornerAngleInput(String(angleDegrees)) === undefined
  ) {
    return undefined;
  }

  const previousIndex = (pointIndex - 1 + points.length) % points.length;
  const nextIndex = (pointIndex + 1) % points.length;
  const previousPreviousIndex =
    (previousIndex - 1 + points.length) % points.length;
  const nextNextIndex = (nextIndex + 1) % points.length;
  const original = points[pointIndex];
  const previous = points[previousIndex];
  const next = points[nextIndex];
  const previousPrevious = points[previousPreviousIndex];
  const nextNext = points[nextNextIndex];
  if (!original || !previous || !next || !previousPrevious || !nextNext) {
    return undefined;
  }

  const chordX = next[0] - previous[0];
  const chordY = next[1] - previous[1];
  const chordLength = Math.hypot(chordX, chordY);
  const radians = (angleDegrees * Math.PI) / 180;
  const sine = Math.sin(radians);
  if (!(chordLength > EPSILON) || Math.abs(sine) < EPSILON) return undefined;

  const midpoint: Point2D = [
    (previous[0] + next[0]) / 2,
    (previous[1] + next[1]) / 2,
  ];
  const normal: Point2D = [-chordY / chordLength, chordX / chordLength];
  const centerOffset = chordLength / (2 * Math.tan(radians));
  const radius = chordLength / (2 * Math.abs(sine));
  const centers: Point2D[] = [
    [
      midpoint[0] + normal[0] * centerOffset,
      midpoint[1] + normal[1] * centerOffset,
    ],
    [
      midpoint[0] - normal[0] * centerOffset,
      midpoint[1] - normal[1] * centerOffset,
    ],
  ];

  const lockedNeighbors = [previousIndex, nextIndex]
    .flatMap((index) => {
      const timestamp = input.manualAngleTimestamps?.get(index);
      const angle = getPolygonCornerAngleDegrees(points, index);
      return timestamp !== undefined && angle !== undefined
        ? [{ index, timestamp, angle }]
        : [];
    })
    .sort((left, right) => right.timestamp - left.timestamp);

  const candidates: Point2D[] = [];
  for (const center of centers) {
    const originalOffsetX = original[0] - center[0];
    const originalOffsetY = original[1] - center[1];
    const originalOffsetLength = Math.hypot(originalOffsetX, originalOffsetY);
    if (originalOffsetLength > EPSILON) {
      candidates.push([
        center[0] + (originalOffsetX / originalOffsetLength) * radius,
        center[1] + (originalOffsetY / originalOffsetLength) * radius,
      ]);
    }

    for (let step = 0; step < 720; step += 1) {
      const sampleRadians = (step / 720) * Math.PI * 2;
      candidates.push([
        center[0] + Math.cos(sampleRadians) * radius,
        center[1] + Math.sin(sampleRadians) * radius,
      ]);
    }

    for (const lock of lockedNeighbors) {
      const lockVertex = points[lock.index];
      const fixedNeighbor =
        lock.index === previousIndex ? previousPrevious : nextNext;
      if (!lockVertex) continue;
      for (const direction of getPreservedAngleRays(
        lockVertex,
        fixedNeighbor,
        lock.angle,
      )) {
        candidates.push(
          ...rayCircleIntersections({
            origin: lockVertex,
            direction,
            center,
            radius,
          }),
        );
      }
    }
  }

  const originalCross =
    (previous[0] - original[0]) * (next[1] - original[1]) -
    (previous[1] - original[1]) * (next[0] - original[0]);
  const validCandidates = candidates.flatMap((candidate) => {
    if (
      distance(previous, candidate) < MIN_SEGMENT_LENGTH_METERS ||
      distance(candidate, next) < MIN_SEGMENT_LENGTH_METERS
    ) {
      return [];
    }
    const candidateCross =
      (previous[0] - candidate[0]) * (next[1] - candidate[1]) -
      (previous[1] - candidate[1]) * (next[0] - candidate[0]);
    if (originalCross * candidateCross <= EPSILON) return [];
    const candidateAngle = getCornerAngleDegrees(candidate, previous, next);
    if (
      candidateAngle === undefined ||
      Math.abs(candidateAngle - angleDegrees) > ANGLE_TOLERANCE_DEGREES
    ) {
      return [];
    }
    const nextPoints = points.map((point, index) =>
      index === pointIndex ? candidate : point,
    );
    if (shapePointsExceedMaxSeparation(nextPoints)) return [];
    return isPolygonSelfIntersecting(nextPoints) ? [] : [candidate];
  });

  const getLockErrors = (candidate: Point2D): number[] => {
    const nextPoints = points.map((point, index) =>
      index === pointIndex ? candidate : point,
    );
    return lockedNeighbors.map((lock) => {
      const nextAngle = getPolygonCornerAngleDegrees(nextPoints, lock.index);
      const error =
        nextAngle === undefined
          ? Number.POSITIVE_INFINITY
          : Math.abs(nextAngle - lock.angle);
      return error < ANGLE_TOLERANCE_DEGREES ? 0 : error;
    });
  };

  const best = validCandidates.reduce<Point2D | undefined>(
    (current, candidate) => {
      if (!current) return candidate;
      const currentErrors = getLockErrors(current);
      const candidateErrors = getLockErrors(candidate);
      for (let index = 0; index < candidateErrors.length; index += 1) {
        const difference =
          (candidateErrors[index] ?? 0) - (currentErrors[index] ?? 0);
        if (Math.abs(difference) > ANGLE_TOLERANCE_DEGREES) {
          return difference < 0 ? candidate : current;
        }
      }
      return distance(candidate, original) < distance(current, original)
        ? candidate
        : current;
    },
    undefined,
  );

  return best ? { pointIndex, nextPoint: best } : undefined;
};

/**
 * Resolve the explicit corner targets used by Cmd/Ctrl-double-click.
 * Fewer than two selected corners keeps the legacy "all eligible corners" scope.
 */
export const resolveSelectedCornerRightAngleTargets = (
  selectedPointIndexes: readonly number[] | undefined,
  doubleClickedPointIndex: number,
): number[] | undefined => {
  if (!selectedPointIndexes || selectedPointIndexes.length < 2) {
    return undefined;
  }
  return [...new Set([...selectedPointIndexes, doubleClickedPointIndex])];
};

/** Set every requested and initially eligible corner to 90°, falling back to compatible corners. */
export const planEligibleCornerRightAngles = (
  points: readonly Point2D[],
  requestedPointIndexes?: readonly number[],
): MultipleCornerRightAnglePlan | undefined => {
  const requestedIndexes = requestedPointIndexes
    ? new Set(requestedPointIndexes)
    : undefined;
  const eligibleIndexes = points.flatMap((_, pointIndex) => {
    if (requestedIndexes && !requestedIndexes.has(pointIndex)) return [];
    const angle = getPolygonCornerAngleDegrees(points, pointIndex);
    return angle !== undefined && angle >= 45 && angle <= 135
      ? [pointIndex]
      : [];
  });
  if (!eligibleIndexes.length) return undefined;

  const tryPlan = (
    seed: readonly Point2D[],
    indexes: readonly number[],
  ): Point2D[] | undefined => {
    let nextPoints = seed.map((point) => [...point] as Point2D);
    for (let pass = 0; pass < 60; pass += 1) {
      let changed = false;
      for (const pointIndex of indexes) {
        const angle = getPolygonCornerAngleDegrees(nextPoints, pointIndex);
        if (
          angle !== undefined &&
          Math.abs(angle - 90) <= RIGHT_ANGLE_COMPLETION_TOLERANCE_DEGREES
        ) {
          continue;
        }
        const plan = planCornerAngleEdit({
          points: nextPoints,
          pointIndex,
          angleDegrees: 90,
        });
        if (!plan) continue;
        nextPoints = nextPoints.map((point, index) =>
          index === pointIndex ? plan.nextPoint : point,
        );
        changed = true;
      }
      const complete = indexes.every((pointIndex) => {
        const angle = getPolygonCornerAngleDegrees(nextPoints, pointIndex);
        return (
          angle !== undefined &&
          Math.abs(angle - 90) <= RIGHT_ANGLE_COMPLETION_TOLERANCE_DEGREES
        );
      });
      if (complete && !isPolygonSelfIntersecting(nextPoints)) return nextPoints;
      if (!changed) break;
    }
    return undefined;
  };

  const allPoints = tryPlan(points, eligibleIndexes);
  if (allPoints) {
    return { nextPoints: allPoints, pointIndexes: eligibleIndexes };
  }

  // Some combinations cannot all be satisfied by moving vertices. Keep every
  // corner that can be added without disturbing already accepted right angles.
  let nextPoints = points.map((point) => [...point] as Point2D);
  const accepted: number[] = [];
  for (const pointIndex of eligibleIndexes) {
    const candidate = tryPlan(nextPoints, [...accepted, pointIndex]);
    if (!candidate) continue;
    nextPoints = candidate;
    accepted.push(pointIndex);
  }
  return accepted.length ? { nextPoints, pointIndexes: accepted } : undefined;
};

/** Finish a corner whose right-angle bracket is lit, moving only that vertex. */
export const planReleasedCornerRightAngle = (
  points: readonly Point2D[],
  pointIndex: number,
): CornerAngleEditPlan | undefined => {
  const angle = getPolygonCornerAngleDegrees(points, pointIndex);
  if (
    angle === undefined ||
    Math.abs(angle - 90) >= RIGHT_ANGLE_INDICATOR_TOLERANCE_DEGREES
  )
    return undefined;
  return planCornerAngleEdit({ points, pointIndex, angleDegrees: 90 });
};

/** Finish only selected corners whose right-angle bracket was lit at release. */
export const planReleasedCornerRightAngles = (
  points: readonly Point2D[],
  selectedPointIndexes: readonly number[],
): MultipleCornerRightAnglePlan | undefined => {
  const litIndexes = selectedPointIndexes.filter((pointIndex) => {
    const angle = getPolygonCornerAngleDegrees(points, pointIndex);
    return (
      angle !== undefined &&
      Math.abs(angle - 90) < RIGHT_ANGLE_INDICATOR_TOLERANCE_DEGREES
    );
  });
  if (!litIndexes.length) return undefined;
  return planEligibleCornerRightAngles(points, litIndexes);
};
